import React, { useState } from 'react';
import { Grid, Paper, Button, Typography } from '@mui/material';
import InventorySelector from './InventorySelector';
import ResultsDisplay from './ResultsDisplay';

// --- Import Mock Data ---
import { seeds } from '../mock-data/seeds';
import { animals } from '../mock-data/animals';
import { beds } from '../mock-data/beds';
import { animalFood } from '../mock-data/animalFood';

const OptimizerPage = () => {
  const [userInventory, setUserInventory] = useState({
    seeds: {},
    animals: {},
    beds: {},
  });
  const [calculationResult, setCalculationResult] = useState(null);

  const handleCalculate = () => {
    // --- DEBUGGING ---
    console.clear();
    console.log("--- Starting Calculation ---");
    console.log("Current Inventory State:", userInventory);
    // --- END DEBUGGING ---

    let bestCombination = null;
    let maxProfitRate = -1;

    const ownedSeeds = [];
    for (const seedId in userInventory.seeds) {
      for (const grade in userInventory.seeds[seedId]) {
        if (userInventory.seeds[seedId][grade] > 0) {
          ownedSeeds.push({ id: seedId, grade });
        }
      }
    }

    const ownedAnimals = [];
     for (const animalId in userInventory.animals) {
      for (const grade in userInventory.animals[animalId]) {
        if (userInventory.animals[animalId][grade] > 0) {
          ownedAnimals.push({ id: animalId, grade });
        }
      }
    }

    const ownedBeds = [];
    let totalBeds = 0;
     for (const bedId in userInventory.beds) {
      for (const grade in userInventory.beds[bedId]) {
        const count = userInventory.beds[bedId][grade];
        if (count > 0) {
          ownedBeds.push({ id: bedId, grade, count });
          totalBeds += count;
        }
      }
    }
    
    // --- DEBUGGING ---
    console.log("Found Owned Seeds:", ownedSeeds);
    console.log("Found Owned Animals:", ownedAnimals);
    console.log("Found Owned Beds:", ownedBeds);
    // --- END DEBUGGING ---

    if (totalBeds === 0 || ownedSeeds.length === 0 || ownedAnimals.length === 0) {
      alert("Please enter at least one seed, one animal, and one bed to run calculations.");
      setCalculationResult(null); // Explicitly clear previous results
      return;
    }

    ownedSeeds.forEach(seedInv => {
      ownedBeds.forEach(bedInv => {
        ownedAnimals.forEach(animalInv => {
          
          const grade = seedInv.grade;
          // --- DEBUGGING ---
          console.log(`\n> Testing combination: ${seedInv.grade} ${seedInv.id} -> ${bedInv.grade} ${bedInv.id} -> ${animalInv.grade} ${animalInv.id}`);
          // --- END DEBUGGING ---

          if (animalInv.grade !== grade) {
            // --- DEBUGGING ---
            console.log(`  SKIPPED: Grade mismatch (Seed is ${grade}, Animal is ${animalInv.grade})`);
            // --- END DEBUGGING ---
            return;
          }
          
          const seedData = seeds.find(s => s.id === seedInv.id);
          const bedData = beds.find(b => b.id === bedInv.id);
          const animalData = animals.find(a => a.id === animalInv.id);
          const foodData = animalFood.find(f => f.animalId === animalInv.id);
          
          const seedGradeInfo = seedData.grades[grade];
          const bedProduction = bedData.productionPerHarvest[bedInv.grade];
          const foodGradeInfo = foodData.productionPrice[grade];
          const animalProduction = animalData.productionsPerHarvest[animalInv.grade];

          const plantsNeededForFood = foodData.plantsNeeded;
          const plantCyclesNeeded = Math.ceil(plantsNeededForFood / bedProduction);
          const timeToGrowPlants = plantCyclesNeeded * seedGradeInfo.plantingDelay;
          const timeToProduceAnimalProduct = animalData.productionTime;
          const totalCycleTime = timeToGrowPlants + timeToProduceAnimalProduct;
          const cycleProfit = foodGradeInfo.sellingPrice * animalProduction;

          if (totalCycleTime > 0) {
            const profitRate = cycleProfit / totalCycleTime;

            if (profitRate > maxProfitRate) {
              maxProfitRate = profitRate;
              bestCombination = {
                seed: `${seedData.name} (${grade})`,
                bed: `${bedData.name} (${bedInv.grade})`,
                animal: `${animalData.name} (${animalInv.grade})`,
                profitPerHour: Math.round(profitRate * 3600),
                totalBeds: totalBeds,
              };
               // --- DEBUGGING ---
              console.log(`  SUCCESS: Found new best profit rate! ${bestCombination.profitPerHour}/hr`);
              // --- END DEBUGGING ---
            }
          }
        });
      });
    });
    
    // --- DEBUGGING ---
    console.log("--- Calculation Finished ---");
    console.log("Final Best Combination:", bestCombination);
    // --- END DEBUGGING ---

    setCalculationResult(bestCombination);
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>Enter Your Inventory</Typography>
          <InventorySelector
            gameData={{ seeds, animals, beds }}
            userInventory={userInventory}
            setUserInventory={setUserInventory}
          />
        </Grid>
        
        <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
          <Button variant="contained" color="primary" size="large" onClick={handleCalculate}>
            Calculate Best Profit Rate
          </Button>
        </Grid>

        {calculationResult !== null && (
          <Grid item xs={12}>
            <ResultsDisplay result={calculationResult} />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default OptimizerPage;